import {
  Credenza,
  CredenzaBody,
  CredenzaTrigger,
  CredenzaContent,
  CredenzaFooter,
  CredenzaHeader,
  CredenzaTitle,
  CredenzaDescription,
} from '@/components/ui/credenza.tsx';
import { useMediaQuery } from '@/hooks/use-media-query.tsx';
import { cn } from '@/utils/utils.ts';
import { ReactNode, useMemo, useState } from 'react';
import { TPlant } from '@/features/plants/schemas/plants.schema.ts';
import { UserPlantsAccessManagementDesktop } from './user-plant-access-management-desktop.tsx';
import { UserPlantAccessManagementMobile } from './user-plant-access-management-mobile.tsx';
import { Skeleton } from '@/components/ui/skeleton.tsx';
import { Button } from '@/components/ui/button.tsx';
import { LoadingSpinner } from '@/components/ui/loading-spinner.tsx';
import { ListRestart } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip.tsx';
import { useQueryClient } from '@tanstack/react-query';
import { useGetUserPlantsQuery } from '@/features/users/hooks/queries/[user]/list-plants.ts';
import { useGetPlantsQuery } from '@/features/plants/hooks/queries/use-get-plants.tsx';
import { TUser } from '@/features/users/schemas/users.schema.ts';
import { useAssignPlantsToUser } from '@/features/users/hooks/mutations/[user]/assign-plants-to-user.ts';
import { StatusAlert } from '@/features/auth/components/status-alert.tsx';
import { isUserEligibleForAssignment } from '@/features/users/schemas/users.utils.ts';

export type TPlantWithAccessControl = TPlant & {
  columnId: string;
  newlyAdded: boolean;
};

export const UserPlantsAccessManagementDialog = ({
  user,
  children,
}: {
  user: TUser;
  children: ReactNode;
}) => {
  const queryClient = useQueryClient();
  const isMobile = useMediaQuery('(max-width:640px)');
  const [isUserPlantsManagementOpen, setIsUserPlantsManagementOpen] = useState(false);
  const [pendingMoves, setPendingMoves] = useState<Map<string, string>>(new Map());

  const updateUserPlantsQueryMutation = useAssignPlantsToUser({
    queryClient,
    userId: user.userId,
    onSuccess: () => {
      setIsUserPlantsManagementOpen(false);
    },
  });

  const { data: availablePlants, isLoading: isAvailablePlantsLoading } = useGetPlantsQuery({
    enabled: isUserPlantsManagementOpen,
  });

  const { data: assignedUserPlants, isLoading: isUserPlantsLoading } = useGetUserPlantsQuery({
    userId: user.userId,
    enabled: Boolean(user.userId) && isUserPlantsManagementOpen,
  });

  const isLoading = isAvailablePlantsLoading || isUserPlantsLoading;
  const canAssign = isUserEligibleForAssignment(user);

  const plants = useMemo<TPlantWithAccessControl[]>(() => {
    return (availablePlants ?? []).map((p) => {
      const serverColumnId = assignedUserPlants?.plantIds?.includes(p.plantId)
        ? 'associated-plants'
        : 'available-plants';
      const overriddenColumnId = pendingMoves.get(p.plantId);
      return {
        ...p,
        columnId: overriddenColumnId ?? serverColumnId,
        newlyAdded: overriddenColumnId !== undefined && overriddenColumnId !== serverColumnId,
      };
    });
  }, [availablePlants, assignedUserPlants, pendingMoves]);

  const handleMovePlant = (plantId: string, targetColumnId: string) => {
    setPendingMoves((prev) => {
      const next = new Map(prev);
      next.set(plantId, targetColumnId);
      return next;
    });
  };

  return (
    <Credenza
      open={isUserPlantsManagementOpen}
      onOpenChange={(open) => {
        setIsUserPlantsManagementOpen(open);
        if (!open) setPendingMoves(new Map());
      }}
    >
      <CredenzaTrigger asChild>{children}</CredenzaTrigger>

      <CredenzaContent className="max-w-4xl">
        <CredenzaHeader>
          <CredenzaTitle>
            Plant Access for {user.firstName} {user.lastName}
          </CredenzaTitle>
        </CredenzaHeader>

        <CredenzaBody className={cn('sm:px-0', canAssign ? 'sm:h-[540px]' : 'sm:h-[590px]')}>
          <CredenzaDescription>Assign and manage user plants permissions.</CredenzaDescription>
          {!canAssign && (
            <StatusAlert
              type="warning"
              className="mt-2"
              message={`This user cannot be assigned to plants because their ${
                user.status !== 'Active' && user.role === 'Unknown'
                  ? 'status is not active and role is unknown'
                  : user.status !== 'Active'
                    ? 'status is not active'
                    : 'role is unknown'
              }. You can still unassign existing plants.`}
            />
          )}
          {!isMobile ? (
            <div className="mt-4 h-full">
              <UserPlantsAccessManagementDesktop
                plants={plants}
                onMovePlant={handleMovePlant}
                isLoading={isLoading}
                canAssign={canAssign}
              />
            </div>
          ) : (
            <div className="mt-4 h-full">
              <UserPlantAccessManagementMobile
                plants={plants}
                onMovePlant={handleMovePlant}
                isLoading={isLoading}
                canAssign={canAssign}
              />
            </div>
          )}
        </CredenzaBody>
        <CredenzaFooter className="sm:h-9">
          {isLoading && !isMobile && <Skeleton className="h-9 w-[62px]" />}
          {isLoading && isMobile && <Skeleton className="h-9"></Skeleton>}

          {!isLoading && (
            <div className="flex gap-2">
              {isMobile ? (
                <Button
                  variant="secondary"
                  disabled={updateUserPlantsQueryMutation.isPending}
                  onClick={() => setPendingMoves(new Map())}
                  className="p-1 px-1.5"
                >
                  <ListRestart className="h-6 w-6" />
                </Button>
              ) : (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="secondary"
                        disabled={updateUserPlantsQueryMutation.isPending}
                        onClick={() => setPendingMoves(new Map())}
                        className="p-1 px-1.5"
                      >
                        <ListRestart className="h-6 w-6" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent className="bg-slate-50 text-base text-black shadow-xs">
                      <p>Reset plant access list</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}

              <Button
                disabled={updateUserPlantsQueryMutation.isPending}
                onClick={() => {
                  updateUserPlantsQueryMutation.mutate({
                    userId: user.userId,
                    plantIds: plants
                      .filter((p) => p.columnId === 'associated-plants')
                      .map((p) => p.plantId),
                  });
                }}
                className="flex w-full min-w-20 justify-center gap-1 text-center"
              >
                {updateUserPlantsQueryMutation.isPending && <LoadingSpinner size={24} />}
                <span className="text-[16px] select-none">Save</span>
              </Button>
            </div>
          )}
        </CredenzaFooter>
      </CredenzaContent>
    </Credenza>
  );
};
